import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AudioRecordButton from "../form/AudioRecordButton";
import ConfirmDialog from "../modal/ConfirmDialog";
import { formatDateTime } from "../../utils/dateFormat";

function CommentThread({ comments = [], canManageComment = () => false, onAdd, onUpdate, onDelete }) {
  const { t } = useTranslation();
  const [commentText, setCommentText] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [submittingComment, setSubmittingComment] = useState(false);
  const safeComments = comments || [];

  const handleAddComment = async () => {
    const text = commentText.trim();
    if (!text) return;

    setSubmittingComment(true);
    try {
      const result = await onAdd(text);
      if (!result?.error) {
        setCommentText("");
      }
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleStartEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.text || "");
  };

  const handleUpdateComment = async (commentId) => {
    const text = editingCommentText.trim();
    if (!text) return;

    setSubmittingComment(true);
    try {
      const result = await onUpdate(commentId, text);
      if (!result?.error) {
        setEditingCommentId(null);
        setEditingCommentText("");
      }
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText("");
  };

  const handleDeleteComment = async (commentId) => {
    setShowConfirmDelete(null);
    await onDelete(commentId);
  };

  return (
    <>
      {safeComments.map((comment) => {
        const canManage = canManageComment(comment);
        const isEditing = editingCommentId === comment.id;

        return (
          <div key={comment.id} className="mb-2 p-2 bg-light rounded">
            <div className="d-flex justify-content-between align-items-start">
              <small className="text-muted">
                {comment.created_by?.name}
                {comment.created_at && <> &middot; {formatDateTime(comment.created_at)}</>}
                {comment.updated_at && comment.updated_at !== comment.created_at && (
                  <> &middot; {t("comments.edited")}</>
                )}
              </small>
              {canManage && !isEditing && (
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 me-1"
                    title={t("common.edit")}
                    onClick={() => handleStartEditComment(comment)}
                  >
                    <i className="fa fa-pencil" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 text-danger"
                    title={t("common.delete")}
                    onClick={() => setShowConfirmDelete(comment.id)}
                  >
                    <i className="fa fa-trash" />
                  </button>
                </div>
              )}
            </div>
            {isEditing ? (
              <div className="mt-1">
                <textarea
                  className="form-control form-control-sm mb-1"
                  rows={2}
                  value={editingCommentText}
                  onChange={(e) => setEditingCommentText(e.target.value)}
                  disabled={submittingComment}
                />
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={!editingCommentText.trim() || submittingComment}
                    onClick={() => handleUpdateComment(comment.id)}
                  >
                    {t("common.save")}
                  </button>
                  <button type="button" className="btn btn-sm btn-default" onClick={handleCancelEditComment}>
                    {t("common.cancel")}
                  </button>
                  <AudioRecordButton
                    disabled={submittingComment}
                    onTranscription={(text) => setEditingCommentText((prev) => (prev ? `${prev} ${text}` : text))}
                  />
                </div>
              </div>
            ) : (
              <div style={{ whiteSpace: "pre-wrap" }}>{comment.text}</div>
            )}
          </div>
        );
      })}
      <div className="mt-3">
        <textarea
          className="form-control form-control-sm mb-2"
          rows={2}
          placeholder={t("comments.add_placeholder")}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          disabled={submittingComment}
        />
        <div className="btn-group">
          <button
            type="button"
            className="btn btn-sm btn-red"
            disabled={!commentText.trim() || submittingComment}
            onClick={handleAddComment}
          >
            {t("comments.add")}
          </button>
          <AudioRecordButton
            disabled={submittingComment}
            onTranscription={(text) => setCommentText((prev) => (prev ? `${prev} ${text}` : text))}
          />
        </div>
      </div>
      {showConfirmDelete && (
        <ConfirmDialog
          title={t("comments.delete")}
          message={t("comments.confirm_delete")}
          confirmLabel={t("common.delete")}
          confirmClassName="btn btn-danger"
          onCancel={() => setShowConfirmDelete(null)}
          onConfirm={() => handleDeleteComment(showConfirmDelete)}
        />
      )}
    </>
  );
}

CommentThread.propTypes = {
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      text: PropTypes.string,
      created_at: PropTypes.string,
      updated_at: PropTypes.string,
      created_by: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    }),
  ),
  canManageComment: PropTypes.func,
  onAdd: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default CommentThread;
